import React, {useEffect, useRef, useState} from "react";
import {Device} from "mediasoup-client";
import {io} from "socket.io-client";
import StaffWarningPicker from "./StaffWarningPicker";
import {VIEWER_WARNINGS} from "./staffLiveWarnings";

export default function StaffLivePreview({roomName, viewerCount, liveId}) {
  const videoRef = useRef(null);
  const [status, setStatus] = useState("Connexion au live…");
  const [sound, setSound] = useState(false);
  const [messages, setMessages] = useState([]);
  const [warningBusy, setWarningBusy] = useState("");
  const [selectedViewer, setSelectedViewer] = useState(null);
  const [warningResult, setWarningResult] = useState("");

  const warnViewer = async (item, selectedReason) => {
    const cleanReason = String(selectedReason || "").trim();
    if (!cleanReason || cleanReason.length > 500) {
      setWarningResult("Indique un motif de 1 à 500 caractères.");
      return;
    }

    setWarningBusy(item.id);
    setWarningResult("");
    try {
      const token = localStorage.getItem("evoAccessToken") || "";
      const response = await fetch(
        `/api/staff/lives/${liveId}/warn-viewer`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            message_id: item.id,
            pseudo: item.pseudo,
            reason: cleanReason
          })
        }
      );
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || "Envoi impossible.");
      }
      setWarningResult(
        `Avertissement enregistré pour @${item.pseudo}.`
      );
    } catch (error) {
      setWarningResult(error.message || "Envoi impossible.");
    } finally {
      setWarningBusy("");
    }
  };

  useEffect(() => {
    let active = true;
    let transport = null;
    const consumers = new Map();
    const socket = io(window.location.origin, {
      path: "/staff-live-socket/",
      transports: ["websocket"],
      autoConnect: false
    });

    const send = (event, payload) => new Promise((resolve, reject) => {
      socket.timeout(10000).emit(event, payload, (error, response) => {
        if (error || !response?.success) {
          reject(new Error(response?.error || error?.message || event));
        } else {
          resolve(response);
        }
      });
    });

    const showTracks = () => {
      if (!active || !videoRef.current) return;
      const tracks = [...consumers.values()]
        .filter(consumer => !consumer.closed)
        .map(consumer => consumer.track);
      videoRef.current.srcObject = new MediaStream(tracks);
      if (tracks.some(track => track.kind === "video")) {
        setStatus("");
      } else {
        setStatus("En attente de la vidéo du créateur…");
      }
      videoRef.current.play().catch(() => {});
    };

    const connect = async () => {
      try {
        const joined = await send("joinRoom", {
          roomId: roomName,
          email: `staff-preview-${Date.now()}@evo.local`,
          role: "staff",
          pseudo: "Staff EVO"
        });
        if (!active) return;

        const device = new Device();
        await device.load({
          routerRtpCapabilities: joined.rtpCapabilities
        });

        const created = await send("createWebRtcTransport", {
          roomId: roomName
        });
        if (!active) return;
        transport = device.createRecvTransport(created.params);

        transport.on("connect", ({dtlsParameters}, done, fail) => {
          send("connectTransport", {
            roomId: roomName,
            transportId: transport.id,
            dtlsParameters
          }).then(done).catch(fail);
        });

        const addProducer = async producer => {
          const producerId = producer.producerId;
          const role = producer.role || producer.appData?.role;
          if (!active || role !== "host" ||
              !producerId || consumers.has(producerId)) return;

          const result = await send("consume", {
            roomId: roomName,
            transportId: transport.id,
            producerId,
            rtpCapabilities: device.rtpCapabilities
          });
          if (!active) return;

          const consumer = await transport.consume(result.params);
          consumers.set(producerId, consumer);
          showTracks();
          await send("resumeConsumer", {
            roomId: roomName,
            consumerId: consumer.id
          });
        };

        socket.on("producerClosed", ({producerId}) => {
          consumers.get(producerId)?.close();
          consumers.delete(producerId);
          showTracks();
        });

        socket.on("newProducer", producer => {
          addProducer(producer).catch(error => {
            if (active) setStatus(error.message);
          });
        });

        const list = await send("getProducers", {roomId: roomName});
        for (const producer of list.producers || []) {
          await addProducer(producer);
        }
        if (active && consumers.size === 0) {
          setStatus("En attente de la vidéo du créateur…");
        }
      } catch (error) {
        if (active) setStatus(`Lecture impossible : ${error.message}`);
      }
    };

    socket.on("newChatMessage", data => {
      if (data.roomId && String(data.roomId) !== String(roomName)) return;
      if (data.system === true || data.giftData) return;
      const message = String(data.message || data.msg || "").trim();
      if (!message) return;

      const id = String(data.id || `message-${Date.now()}-${Math.random()}`);
      const pseudo = String(
        data.pseudo || data.username || data.name || "Participant"
      ).trim();

      setMessages(previous => {
        if (previous.some(item => item.id === id)) return previous;
        return [...previous, {id, pseudo, message}].slice(-80);
      });
    });

    socket.on("live:message-remove", data => {
      if (data.roomId && String(data.roomId) !== String(roomName)) return;
      setMessages(previous => previous.filter(
        item => item.id !== String(data.messageId || "")
      ));
    });

    socket.on("connect", connect);
    socket.on("connect_error", error => {
      if (active) setStatus(`Connexion impossible : ${error.message}`);
    });
    socket.connect();

    return () => {
      active = false;
      consumers.forEach(consumer => consumer.close());
      transport?.close();
      socket.disconnect();
      if (videoRef.current) videoRef.current.srcObject = null;
    };
  }, [roomName]);

  return (
    <div style={{
      display: "flex", gap: 14, flexWrap: "wrap", marginTop: 12
    }}>
      <div style={{width: "min(100%, 340px)"}}>
        <div style={{color: "#fff", marginBottom: 8, fontWeight: 800}}>
          👥 {viewerCount} spectateur(s) en direct
        </div>
        <video ref={videoRef} autoPlay playsInline muted={!sound}
          style={{
            display: "block", width: "100%", aspectRatio: "9 / 16",
            maxHeight: 500, objectFit: "contain", background: "#000",
            borderRadius: 12
          }}
        />
        {status && <p style={{color: "#b8d3e0"}}>{status}</p>}
        <button type="button" onClick={() => setSound(value => !value)}
          style={{
            marginTop: 8, padding: "8px 12px", cursor: "pointer",
            color: "#fff", background: "#174357",
            border: "1px solid #00eaff", borderRadius: 8
          }}>
          {sound ? "Couper le son" : "Activer le son"}
        </button>
      </div>

      <section style={{
        flex: "1 1 270px", minHeight: 300, maxHeight: 500,
        overflowY: "auto", background: "#04111c",
        border: "1px solid #28607a", borderRadius: 12,
        padding: 12, overflowWrap: "anywhere"
      }}>
        <h3 style={{marginTop: 0}}>💬 Commentaires en direct</h3>
        <p style={{color: "#9bb8c8", fontSize: 12}}>
          Messages reçus depuis l’ouverture de cette fenêtre.
        </p>
        {warningResult && (
          <p role="status" style={{color: "#ffcf6a"}}>
            {warningResult}
          </p>
        )}
        {messages.length === 0 && (
          <p style={{color: "#9bb8c8"}}>Aucun nouveau commentaire.</p>
        )}
        {messages.map(item => (
          <p key={item.id} style={{margin: "9px 0", color: "#fff"}}>
            <strong style={{color: "#00eaff"}}>@{item.pseudo} : </strong>
            {item.message}
            {/^live_message_[0-9]{13}_[A-Za-z0-9_-]{8,128}$/.test(item.id) && (
              <button type="button"
                disabled={Boolean(warningBusy)}
                onClick={() => setSelectedViewer(item)}
                style={{
                  display: "block", marginTop: 5, padding: "5px 9px",
                  color: "#ffcf6a", background: "#30261a",
                  border: "1px solid #ffcf6a", borderRadius: 7,
                  cursor: "pointer"
                }}>
                {warningBusy === item.id
                  ? "Envoi…"
                  : `Avertir @${item.pseudo}`}
              </button>
            )}
          </p>
        ))}
      </section>
      {selectedViewer && (
        <StaffWarningPicker
          title={`Avertir @${selectedViewer.pseudo}`}
          warnings={VIEWER_WARNINGS}
          onClose={() => setSelectedViewer(null)}
          onSelect={reason => {
            const viewer = selectedViewer;
            setSelectedViewer(null);
            warnViewer(viewer, reason);
          }}
        />
      )}
    </div>
  );
}
